import React from 'react';
import { cn } from '../../utils/cn';

type CardVariant = 'default' | 'interactive' | 'outline';
type CardPadding = 'none' | 'sm' | 'md' | 'lg';

interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  children: React.ReactNode;
  variant?: CardVariant;
  padding?: CardPadding;
  className?: string;
}

interface CardButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: React.ReactNode;
  variant?: CardVariant;
  padding?: CardPadding;
  className?: string;
}

export function Card({ 
  children, 
  className = '', 
  variant = 'default',
  padding = 'md',
  ...props 
}: CardProps) {
  const variants = {
    default: 'bg-white border border-gray-100 shadow-sm',
    interactive: cn(
      'bg-white border border-gray-100 shadow-sm',
      'hover:border-condo-dark hover:shadow-md transition-all',
      'focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2'
    ),
    outline: 'bg-white border border-gray-200'
  };

  const paddings = {
    none: '',
    sm: 'p-3',
    md: 'p-3 sm:p-8',
    lg: 'p-4 sm:p-10'
  };

  if (variant === 'interactive') {
    return (
      <button 
        type="button"
        className={cn(
          'rounded-xl',
          variants[variant],
          paddings[padding],
          className
        )}
        {...(props as CardButtonProps)}
      >
        {children}
      </button>
    );
  }

  return (
    <div 
      className={cn(
        'rounded-xl',
        variants[variant],
        paddings[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  padding?: CardPadding;
}

export function CardHeader({ children, padding = 'md', className = '', ...props }: CardHeaderProps) {
  const paddings = {
    none: '',
    sm: 'pb-3',
    md: 'pb-3 sm:pb-4',
    lg: 'pb-3 sm:pb-6'
  };

  return (
    <div 
      className={cn(
        paddings[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  padding?: CardPadding;
}

export function CardContent({ children, padding = 'md', className = '', ...props }: CardContentProps) {
  const paddings = {
    none: '',
    sm: 'pt-0',
    md: 'pt-0 sm:pt-0',
    lg: 'pt-0 sm:pt-0'
  };

  return (
    <div 
      className={cn(
        paddings[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// CardTitle komponent - flyttet fra CardTitle.tsx
type CardTitleSize = 'sm' | 'md' | 'lg';

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  size?: CardTitleSize;
  className?: string;
  iconVariant?: 'default' | 'light' | 'dark';
}

export function CardTitle({ 
  children, 
  icon: Icon, 
  size = 'md',
  iconVariant = 'default',
  className = '', 
  ...props 
}: CardTitleProps) {
  const titleSizes = {
    sm: 'text-xs sm:text-base',
    md: 'text-sm sm:text-xl',
    lg: 'text-base sm:text-xl'
  };

  // Universelle ikon-standarder
  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4', 
    lg: 'h-5 w-5'
  };

  const iconPaddings = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };

  // Universelle ikon-farger
  const iconVariants = {
    default: {
      container: 'bg-condo-dark',
      icon: 'text-condo-light'
    },
    light: {
      container: 'bg-condo-light',
      icon: 'text-condo-dark'
    },
    dark: {
      container: 'bg-condo-dark',
      icon: 'text-condo-light'
    }
  };

  return (
    <div className="flex items-center gap-3 sm:gap-3">
      {Icon && (
        <div className={cn(
          'rounded-lg hidden sm:inline',
          iconPaddings[size],
          iconVariants[iconVariant].container
        )}>
          <Icon className={cn(iconSizes[size], iconVariants[iconVariant].icon)} />
        </div>
      )}
      <h3 
        className={cn(
          titleSizes[size],
          'font-semibold text-gray-900',
          className
        )}
        {...props}
      >
        {children}
      </h3>
    </div>
  )
}